import * as React from "react";
import { TextField, InputAdornment, IconButton } from "@mui/material";

import { CalendarIcon } from "@components/Icons";

import { formatDisplayDateTime } from "@components/CalendarDateTime/calendar.helpers";
import { useCalendarDateTime } from "@components/CalendarDateTime/useCalendarDateTime";
import CalendarDateTimeDialog from "@components/CalendarDateTime/CalendarDateTimeDialog";

type PeriodCalendarFieldProps = {
  value: [Date | null, Date | null];
  onChange: (next: [Date | null, Date | null]) => void;
  label?: string;
  startTitle?: string;
  endTitle?: string;
};

type Step = "start" | "end" | null;

const PeriodsCalendarField: React.FC<PeriodCalendarFieldProps> = ({
  value: externalPeriod,
  onChange,
  label = "Período",
  startTitle = "Vigência inicial",
  endTitle = "Vigência final",
}) => {
  const [step, setStep] = React.useState<Step>(null);

  // estado interno para controlar o "wizard"
  const [internalPeriod, setInternalPeriod] = React.useState<
    [Date | null, Date | null]
  >([externalPeriod[0], externalPeriod[1]]);

  // sincroniza quando o pai muda o valor de fora
  React.useEffect(() => {
    setInternalPeriod([externalPeriod[0], externalPeriod[1]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPeriod[0]?.getTime(), externalPeriod[1]?.getTime()]);

  const [startInternal, endInternal] = internalPeriod;

  /**
   * CONTROLE DA DATA/HORA INICIAL
   * - aqui o onChange NÃO chama o onChange do pai ainda
   * - só atualizamos o estado interno
   */
  const startController = useCalendarDateTime({
    value: startInternal ? startInternal.toISOString() : null,
    onChange: (iso) => {
      const nextStart = iso ? new Date(iso) : null;
      setInternalPeriod(([_, end]) => [nextStart, end]);
    },
  });

  /**
   * CONTROLE DA DATA/HORA FINAL
   * - aqui sim, ao confirmar, propagamos ao pai
   */
  const endController = useCalendarDateTime({
    value: endInternal ? endInternal.toISOString() : null,
    onChange: (iso) => {
      const nextEnd = iso ? new Date(iso) : null;

      setInternalPeriod(([start]) => {
        const next: [Date | null, Date | null] = [start ?? null, nextEnd];
        onChange(next); // aqui o filtro é atualizado de fato
        return next;
      });
    },
  });

  const { state: startState, actions: startActions } = startController;

  const { state: endState, actions: endActions } = endController;

  /**
   * Abre o "wizard": começa pela vigência inicial
   */
  const openWizard = () => {
    setStep("start");
    startActions.openDialog();
  };

  const closeWizard = () => {
    setStep(null);
    startActions.closeDialog();
    endActions.closeDialog();
  };

  /**
   * Confirmar INÍCIO:
   * - se data/hora forem válidas, confirma no hook
   * - fecha modal de início
   * - abre modal de fim
   */
  const handleConfirmStart = () => {
    const { selectedDate, selectedTime } = startState;

    if (!selectedDate || !selectedTime) {
      // delega pro hook (ele já bloqueia se estiver incompleto)
      startActions.handleConfirm();
      return;
    }

    startActions.handleConfirm(); // atualiza internalPeriod e fecha o dialog de início
    setStep("end");
    endActions.openDialog();
  };

  /**
   * Confirmar FIM:
   * - hook já valida data/hora,
   * - chama o onChange interno (que propaga ao pai),
   * - fecha o wizard
   */
  const handleConfirmEnd = () => {
    endActions.handleConfirm();
    closeWizard();
  };

  const displayValue =
    internalPeriod[0] && internalPeriod[1]
      ? `${formatDisplayDateTime(internalPeriod[0])} - ${formatDisplayDateTime(
          internalPeriod[1]
        )}`
      : internalPeriod[0]
      ? `A partir de ${formatDisplayDateTime(internalPeriod[0])}`
      : "";

  return (
    <>
      <TextField
        label={label}
        fullWidth
        value={displayValue}
        placeholder="Informe o período"
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={openWizard}>
                <CalendarIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onClick={openWizard}
      />

      {/* Modal de VIGÊNCIA INICIAL */}
      <CalendarDateTimeDialog
        open={startState.open && step === "start"}
        title={startTitle}
        daysMatrix={startState.daysMatrix}
        selectedDateLabel={startState.selectedDateLabel}
        selectedTime={startState.selectedTime}
        onMonthChange={startActions.handleMonthChange}
        onDayClick={startActions.handleDayClick}
        onTimeClick={startActions.handleTimeClick}
        onClear={startActions.handleClear}
        onClose={closeWizard}
        onConfirm={handleConfirmStart}
      />

      {/* Modal de VIGÊNCIA FINAL */}
      <CalendarDateTimeDialog
        open={endState.open && step === "end"}
        title={endTitle}
        daysMatrix={endState.daysMatrix}
        selectedDateLabel={endState.selectedDateLabel}
        selectedTime={endState.selectedTime}
        onMonthChange={endActions.handleMonthChange}
        onDayClick={endActions.handleDayClick}
        onTimeClick={endActions.handleTimeClick}
        onClear={endActions.handleClear}
        onClose={closeWizard}
        onConfirm={handleConfirmEnd}
        minDate={startInternal ?? undefined}
      />
    </>
  );
};

export default PeriodsCalendarField;
